import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    attachConditionToRule,
    attachConditionToTransition,
    type ConditionTreeInput,
    createAction,
    createCondition,
    createLinkingRule,
    createRule,
    createTransition,
    type DefinitionFields,
    deleteAction,
    deleteLinkingRule,
    deleteRule,
    deleteTransition,
    detachCondition,
    dryRun,
    type LinkingRuleFields,
    patchDefinition,
    patchLinkingRule,
    patchRuleParams,
    patchTransition,
    putConditionTree,
    queryKeys,
    reorderActions,
    reorderRules,
    type TransitionInput,
} from "./api";

/**
 * Every write the editor makes, each invalidating the aggregate read on success.
 *
 * Invalidating rather than patching the cache by hand: the aggregate carries derived data the
 * client cannot recompute — rendered condition summaries, resolved reference choices, whether an
 * action is still registered — so a local patch would drift from what the server would say. One
 * refetch after a write is the honest trade, and react-query keeps the stale data on screen while
 * it happens.
 */
export function useEditorMutations(definitionId: string) {
    const queryClient = useQueryClient();

    const invalidate = () => {
        queryClient.invalidateQueries({
            queryKey: queryKeys.definition(definitionId),
        });
    };

    // Metadata changes far less often, but adding an action can change what is still available,
    // and editing the linking rule changes its warnings.
    const invalidateAll = () => {
        invalidate();
        queryClient.invalidateQueries({
            queryKey: queryKeys.metadata(definitionId),
        });
    };

    return {
        saveDefinition: useMutation({
            mutationFn: (fields: Partial<DefinitionFields>) =>
                patchDefinition(definitionId, fields),
            onSuccess: invalidate,
        }),

        addLinkingRule: useMutation({
            mutationFn: (fields: LinkingRuleFields) =>
                createLinkingRule(definitionId, fields),
            onSuccess: invalidateAll,
        }),

        removeLinkingRule: useMutation({
            mutationFn: (id: number) => deleteLinkingRule(id),
            onSuccess: invalidateAll,
        }),

        saveLinkingRule: useMutation({
            mutationFn: ({
                id,
                fields,
            }: {
                id: number;
                fields: Partial<LinkingRuleFields>;
            }) => patchLinkingRule(id, fields),
            onSuccess: invalidateAll,
        }),

        addTransition: useMutation({
            mutationFn: (input: Omit<TransitionInput, "workflow">) =>
                createTransition({ ...input, workflow: definitionId }),
            onSuccess: invalidate,
        }),

        saveTransition: useMutation({
            mutationFn: ({
                id,
                fields,
            }: {
                id: string;
                fields: Partial<Omit<TransitionInput, "workflow">>;
            }) => patchTransition(id, fields),
            onSuccess: invalidate,
        }),

        removeTransition: useMutation({
            mutationFn: (id: string) => deleteTransition(id),
            onSuccess: invalidate,
        }),

        addAction: useMutation({
            mutationFn: ({
                transitionId,
                actionName,
            }: {
                transitionId: string;
                actionName: string;
            }) => createAction(transitionId, actionName),
            onSuccess: invalidate,
        }),

        removeAction: useMutation({
            mutationFn: (id: string) => deleteAction(id),
            onSuccess: invalidate,
        }),

        moveActions: useMutation({
            mutationFn: ({
                transitionId,
                actionIds,
            }: {
                transitionId: string;
                actionIds: string[];
            }) => reorderActions(transitionId, actionIds),
            onSuccess: invalidate,
        }),

        addRule: useMutation({
            mutationFn: (transitionActionId: string) =>
                createRule(transitionActionId),
            onSuccess: invalidate,
        }),

        saveRuleParams: useMutation({
            mutationFn: ({
                id,
                extraParams,
            }: {
                id: string;
                extraParams: Record<string, unknown>;
            }) => patchRuleParams(id, extraParams),
            onSuccess: invalidate,
        }),

        removeRule: useMutation({
            mutationFn: (id: string) => deleteRule(id),
            onSuccess: invalidate,
        }),

        moveRules: useMutation({
            mutationFn: ({
                transitionActionId,
                ruleIds,
            }: {
                transitionActionId: string;
                ruleIds: string[];
            }) => reorderRules(transitionActionId, ruleIds),
            onSuccess: invalidate,
        }),

        /**
         * Create a condition and attach it in one step.
         *
         * Two requests, but never left half-done from the client's point of view: an unattached
         * condition is invisible in the editor and would simply be recreated next time, whereas
         * a caller sequencing these itself could attach the wrong one.
         */
        addCondition: useMutation({
            mutationFn: async ({
                name,
                attachTo,
            }: {
                name: string;
                attachTo:
                    | { kind: "transition"; id: string }
                    | { kind: "rule"; id: string };
            }) => {
                const condition = await createCondition(definitionId, name);
                if (attachTo.kind === "transition") {
                    await attachConditionToTransition(
                        attachTo.id,
                        condition.id,
                    );
                } else {
                    await attachConditionToRule(attachTo.id, condition.id);
                }
                return condition;
            },
            onSuccess: invalidate,
        }),

        saveConditionTree: useMutation({
            mutationFn: ({
                conditionId,
                input,
            }: {
                conditionId: string;
                input: ConditionTreeInput;
            }) => putConditionTree(conditionId, input),
            onSuccess: invalidate,
        }),

        detachCondition: useMutation({
            mutationFn: (conditionId: string) => detachCondition(conditionId),
            onSuccess: invalidate,
        }),

        runDryRun: useMutation({
            mutationFn: (eventId: string) => dryRun(definitionId, eventId),
        }),
    };
}

export type EditorMutations = ReturnType<typeof useEditorMutations>;
